// src/api/mappers/buildCounterfactual.js — VIRAJ SYSTEM v9.2
//
// Канон: UI_CORE_SPEC v9.2 — Counterfactual Layer
// Назначение: вычисляет "ghost" линию для FinanceChartV10 —
// что было бы с бюджетом без вмешательства Engine.
//
// Алгоритм:
//   Для каждого дня в trendData:
//   - если день кризисный (risk_status = critical/red):
//     ghost = spend * (1 + |roi_crisis|)
//     Логика: без Engine spend продолжал бы расти по кризисной траектории
//   - если день action (есть auto_action в этот день):
//     ghost = spend_before_action * (1 + deviation)
//   - иначе: ghost = null (нет контрфактуала)
//
// Используется в FinanceChartV10 как dataKey="ghost"

/**
 * Строит counterfactual данные для chart.
 *
 * @param {object[]} trendData     — массив из /api/finance/trends
 * @param {object[]} riskHistory   — массив из /api/risk/history
 * @param {object[]} actions       — массив из /api/antiloss/actions
 * @returns {object[]}             — trendData с добавленным полем ghost
 */
export function buildCounterfactual(trendData, riskHistory = [], actions = []) {
  if (!Array.isArray(trendData) || !trendData.length) return [];

  // Создаём быстрые lookup-таблицы
  const riskByDate   = {};
  const actionDates  = new Set();

  riskHistory.forEach(r => { riskByDate[r.date] = r; });

  actions.forEach(a => {
    if (!a.timestamp) return;
    const n = Number(a.timestamp);
    const d = n > 1e12
      ? new Date(n).toISOString().slice(0, 10)
      : new Date(n * 1000).toISOString().slice(0, 10);
    actionDates.add(d);
  });

  return trendData.map(d => {
    const date  = d.date;
    const spend = Number(d.spend) || 0;

    // ROI из trends (нормализуем: доля → % если нужно)
    const roi = typeof d.roi === "number" && Math.abs(d.roi) < 10
      ? d.roi * 100
      : Number(d.roi) || 0;

    const risk = riskByDate[date];
    const isCrisis = risk && (risk.risk_status === "critical" || risk.risk_status === "red");
    const isAction = actionDates.has(date);

    let ghost = null;

    if (isCrisis && spend > 0) {
      // Без Engine: spend продолжался бы с убытком |roi|
      const lossRate = Math.abs(roi) / 100;
      ghost = Math.round(spend * (1 + Math.max(lossRate, 0.15)));
    } else if (isAction && spend > 0) {
      // День вмешательства: ghost = что было бы без reduce_spend
      const deviation = Number(risk?.deviation) || 0.2;
      ghost = Math.round(spend * (1 + Math.abs(deviation)));
    }

    return { ...d, ghost };
  });
}

/**
 * Вычисляет суммарную "спасённую" сумму из counterfactual данных.
 *
 * @param {object[]} counterfactualData — результат buildCounterfactual()
 * @returns {number}                    — сумма ghost - spend для всех ghost-дней
 */
export function calcCounterfactualSaved(counterfactualData) {
  return counterfactualData.reduce((sum, d) => {
    if (d.ghost == null) return sum;
    const spend = Number(d.spend) || 0;
    const diff  = d.ghost - spend;
    return sum + Math.max(0, diff);
  }, 0);
}
